import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, LogIn, Shield, ScrollText, Copy, Check, X, Building2, Trash2, RotateCcw } from "lucide-react";
import ConfirmDialog from "../components/ConfirmDialog";
import SystemHealthWidget from "../components/SystemHealthWidget";

const TabBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    onClick={onClick}
    className="se-btn"
    style={{
      background: active ? "#7C5C3A" : "transparent",
      color: active ? "#FDFAF6" : "#3B2F1E",
      border: active ? "none" : "0.5px solid rgba(212,169,106,0.4)",
    }}
    data-testid={testid}
  >
    <Icon size={16} /> {label}
  </button>
);

const empty = { stable_name: "", country: "", admin_full_name: "", admin_email: "" };

export default function SuperAdmin() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stables");
  const [stables, setStables] = useState(null);
  const [audit, setAudit] = useState(null);
  const [trash, setTrash] = useState(null);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(empty);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createResult, setCreateResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmDeleteStable, setConfirmDeleteStable] = useState(null);

  const loadStables = async () => {
    try {
      const { data } = await api.get("/superadmin/stables");
      setStables(data);
    } catch (e) {
      setError(formatApiError(e));
      setStables([]);
    }
  };
  const loadAudit = async () => {
    try {
      const { data } = await api.get("/superadmin/audit-log");
      setAudit(data);
    } catch (e) {
      setError(formatApiError(e));
      setAudit([]);
    }
  };

  const loadTrash = async () => {
    try {
      const { data } = await api.get("/superadmin/trash");
      setTrash(data);
    } catch (e) {
      setError(formatApiError(e));
      setTrash([]);
    }
  };

  useEffect(() => {
    if (tab === "stables") loadStables();
    if (tab === "audit") loadAudit();
    if (tab === "trash") loadTrash();
  }, [tab]);

  const createStable = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateError("");
    try {
      const { data } = await api.post("/superadmin/stables", form);
      setCreateResult(data);
      setForm(empty);
      await loadStables();
    } catch (err) {
      setCreateError(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const impersonate = async (stableId) => {
    try {
      await api.post(`/superadmin/impersonate/${stableId}`);
      await refresh();
      navigate("/dashboard");
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const restore = async (item) => {
    try {
      await api.post(`/superadmin/restore/${item.type}/${item.id}`);
      await loadTrash();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const deleteStable = async (stableId) => {
    try {
      await api.delete(`/superadmin/stables/${stableId}`);
      setConfirmDeleteStable(null);
      await loadStables();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const copyPassword = async () => {
    if (!createResult?.temp_password) return;
    try {
      await navigator.clipboard.writeText(createResult.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  const dismissResult = () => {
    setShowCreate(false);
    setCreateResult(null);
    setCopied(false);
  };

  return (
    <div data-testid="superadmin-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="se-label flex items-center gap-2">
            <Shield size={12} /> Platform owner
          </div>
          <h1 className="font-heading mt-1" style={{ fontSize: 28 }}>Super Admin Console</h1>
          <p className="text-sm mt-1" style={{ color: "#8C9E8A" }}>
            Manage every stable, impersonate any admin, and audit all platform-level actions.
          </p>
        </div>
        {tab === "stables" && (
          <button className="se-btn" onClick={() => { setShowCreate(true); setCreateResult(null); }} data-testid="superadmin-create-stable-button">
            <Plus size={16} /> Create stable
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        <TabBtn active={tab === "stables"} onClick={() => setTab("stables")} icon={Building2} label="Stables" testid="superadmin-tab-stables" />
        <TabBtn active={tab === "audit"} onClick={() => setTab("audit")} icon={ScrollText} label="Audit Log" testid="superadmin-tab-audit" />
        <TabBtn active={tab === "trash"} onClick={() => setTab("trash")} icon={Trash2} label="Trash" testid="superadmin-tab-trash" />
      </div>

      {error && (
        <div
          className="mb-4 text-sm px-3 py-2 rounded-lg"
          style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }}
          data-testid="superadmin-error"
        >
          {error}
        </div>
      )}

      {tab === "stables" && (
        <div className="se-card p-0 overflow-hidden" data-testid="superadmin-stables-card">
          <div className="overflow-x-auto">
            <table className="se-table w-full" data-testid="superadmin-stables-table">
              <thead>
                <tr>
                  <th>Stable</th>
                  <th>Country</th>
                  <th>Admin</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Horses / Owners / Users</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {stables === null && <tr><td colSpan={7} style={{ color: "#8C9E8A" }}>Loading…</td></tr>}
                {stables !== null && stables.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ color: "#8C9E8A" }} data-testid="superadmin-stables-empty">
                      No stables yet. Click "Create stable" to onboard one.
                    </td>
                  </tr>
                )}
                {(stables || []).map((s) => (
                  <tr key={s.id} data-testid={`superadmin-stable-row-${s.id}`}>
                    <td style={{ fontWeight: 600 }}>{s.name}</td>
                    <td>{s.country}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{s.admin_full_name || "—"}</div>
                      <div className="text-xs" style={{ color: "#8C9E8A" }}>{s.admin_email || "—"}</div>
                    </td>
                    <td><span className="se-badge se-badge-inactive">{s.plan}</span></td>
                    <td>
                      <span className={`se-badge ${s.status === "trial" ? "se-badge-warning" : s.status === "active" ? "se-badge-active" : "se-badge-urgent"}`}>
                        {s.status}
                      </span>
                    </td>
                    <td style={{ color: "#8C9E8A", fontVariantNumeric: "tabular-nums" }}>
                      {s.horses_count} / {s.owners_count} / {s.users_count}
                    </td>
                    <td>
                      <div className="flex gap-2 justify-end">
                        <button
                          className="se-btn"
                          style={{ padding: "6px 12px", fontSize: 13 }}
                          onClick={() => impersonate(s.id)}
                          data-testid={`enter-as-stable-${s.id}`}
                        >
                          <LogIn size={14} /> Enter as stable
                        </button>
                        <button
                          className="se-btn"
                          style={{ padding: "6px 10px", fontSize: 13, background: "#C0392B" }}
                          onClick={() => setConfirmDeleteStable(s)}
                          data-testid={`delete-stable-${s.id}`}
                          aria-label="Delete stable"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "audit" && (
        <div className="se-card p-0 overflow-hidden" data-testid="superadmin-audit-card">
          <div className="overflow-x-auto">
            <table className="se-table w-full" data-testid="superadmin-audit-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Actor</th>
                  <th>Stable</th>
                  <th>Action</th>
                  <th>Method / Path</th>
                </tr>
              </thead>
              <tbody>
                {audit === null && <tr><td colSpan={5} style={{ color: "#8C9E8A" }}>Loading…</td></tr>}
                {audit !== null && audit.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ color: "#8C9E8A" }} data-testid="superadmin-audit-empty">
                      No audit entries yet. Actions during impersonation and edit/delete events will appear here.
                    </td>
                  </tr>
                )}
                {(audit || []).map((a) => (
                  <tr key={a.id} data-testid={`audit-row-${a.id}`}>
                    <td style={{ color: "#8C9E8A" }}>{new Date(a.timestamp).toLocaleString()}</td>
                    <td>
                      <div style={{ fontSize: 13 }}>{a.superadmin_email || a.actor_user_id || "—"}</div>
                      <div style={{ fontSize: 11, color: "#8C9E8A" }}>{a.actor_role || ""}</div>
                    </td>
                    <td>{a.stable_name || a.stable_id || "—"}</td>
                    <td style={{ fontWeight: 500 }}>{a.action}</td>
                    <td style={{ color: "#8C9E8A", fontFamily: "monospace", fontSize: 12 }}>
                      {a.method} {a.path}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "trash" && (
        <div className="se-card p-0 overflow-hidden" data-testid="superadmin-trash-card">
          <div className="overflow-x-auto">
            <table className="se-table w-full" data-testid="superadmin-trash-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Stable</th>
                  <th>Deleted at</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {trash === null && <tr><td colSpan={5} style={{ color: "#8C9E8A" }}>Loading…</td></tr>}
                {trash !== null && trash.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ color: "#8C9E8A" }} data-testid="superadmin-trash-empty">
                      No deleted records. Soft-deleted items will appear here for restoration.
                    </td>
                  </tr>
                )}
                {(trash || []).map((t) => (
                  <tr key={`${t.type}-${t.id}`} data-testid={`trash-row-${t.id}`}>
                    <td><span className="se-badge se-badge-inactive">{t.type}</span></td>
                    <td style={{ fontWeight: 600 }}>{t.name}</td>
                    <td>{t.stable_name || "—"}</td>
                    <td style={{ color: "#8C9E8A" }}>
                      {t.deleted_at ? new Date(t.deleted_at).toLocaleString() : "—"}
                    </td>
                    <td>
                      <button
                        className="se-btn"
                        style={{ padding: "6px 10px", fontSize: 13 }}
                        onClick={() => restore(t)}
                        data-testid={`restore-${t.type}-${t.id}`}
                      >
                        <RotateCcw size={14} /> Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && (
        <div
          className="fixed inset-0 z-30 flex items-center justify-center p-4"
          style={{ background: "rgba(59,47,30,0.5)" }}
          onClick={dismissResult}
          data-testid="create-stable-modal-backdrop"
        >
          <div onClick={(e) => e.stopPropagation()} className="se-card w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading" style={{ fontSize: 18 }}>
                {createResult ? "Stable created" : "Create a new stable"}
              </h3>
              <button type="button" onClick={dismissResult} className="se-btn se-btn-ghost" data-testid="create-stable-close">
                <X size={16} />
              </button>
            </div>

            {!createResult && (
              <form onSubmit={createStable} data-testid="create-stable-form">
                <label className="se-label block mb-1">Stable name</label>
                <input className="se-input mb-3" value={form.stable_name} onChange={(e) => setForm({ ...form, stable_name: e.target.value })} required data-testid="create-stable-name" />

                <label className="se-label block mb-1">Country</label>
                <input className="se-input mb-3" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} required data-testid="create-stable-country" />

                <label className="se-label block mb-1">Admin full name</label>
                <input className="se-input mb-3" value={form.admin_full_name} onChange={(e) => setForm({ ...form, admin_full_name: e.target.value })} required data-testid="create-stable-admin-name" />

                <label className="se-label block mb-1">Admin email</label>
                <input className="se-input mb-3" type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} required data-testid="create-stable-admin-email" />

                <p className="text-xs mb-4" style={{ color: "#8C9E8A" }}>
                  A temporary 12-character password will be generated and shown once. Welcome email is currently disabled.
                </p>

                {createError && (
                  <div className="mb-3 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="create-stable-error">
                    {createError}
                  </div>
                )}

                <div className="flex gap-3 justify-end">
                  <button type="button" className="se-btn se-btn-ghost" onClick={dismissResult} data-testid="create-stable-cancel">Cancel</button>
                  <button type="submit" className="se-btn" disabled={creating} data-testid="create-stable-submit">
                    {creating ? "Creating…" : "Create stable"}
                  </button>
                </div>
              </form>
            )}

            {createResult && (
              <div data-testid="create-stable-result">
                <p className="text-sm mb-4" style={{ color: "#3B2F1E" }}>
                  <strong>{createResult.stable.name}</strong> is live. Share these credentials with the admin securely — the password is shown only once.
                </p>

                <div className="se-divider mb-4" />

                <div className="grid grid-cols-3 gap-3 mb-2 text-sm">
                  <div className="se-label" style={{ alignSelf: "center" }}>Login URL</div>
                  <div className="col-span-2" style={{ wordBreak: "break-all" }}>{window.location.origin}/login</div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-2 text-sm">
                  <div className="se-label" style={{ alignSelf: "center" }}>Admin email</div>
                  <div className="col-span-2" data-testid="create-result-email">{createResult.admin.email}</div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
                  <div className="se-label" style={{ alignSelf: "center" }}>Temp password</div>
                  <div className="col-span-2 flex items-center gap-2">
                    <code
                      style={{
                        background: "rgba(212,169,106,0.18)",
                        padding: "6px 10px",
                        borderRadius: 6,
                        fontFamily: "monospace",
                        fontSize: 14,
                        color: "#3B2F1E",
                      }}
                      data-testid="create-result-password"
                    >
                      {createResult.temp_password}
                    </code>
                    <button className="se-btn se-btn-ghost" style={{ padding: "6px 10px" }} onClick={copyPassword} data-testid="create-result-copy">
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button className="se-btn" onClick={dismissResult} data-testid="create-result-done">Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDeleteStable}
        onClose={() => setConfirmDeleteStable(null)}
        onConfirm={() => deleteStable(confirmDeleteStable.id)}
        title={`Delete stable "${confirmDeleteStable?.name}"?`}
        description="The stable and its data will be moved to trash. You can restore it from the Trash tab."
        confirmLabel="Yes, delete"
        testid="delete-stable-confirm"
      />

      {/* System Health widget — super_admin only. Placed at the bottom of the console. */}
      <SystemHealthWidget />
    </div>
  );
}
